<template>
    <div class="sort-options input select">
        <select id="sortSelect" v-model="value" :disabled="disabled" @change="dispatchEvent">
            <option v-for="(option, index) in sortOptions" :key="index" :value="option.value">
                {{ option.label }}
            </option>
        </select>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';

import { buildQuery, getFilterValue } from '~/helpers/queryParser';
import { getFilterOptions, sortValues } from '~/config/filters';
import { LOCATION, SORT } from '~/config/filterKeys';

export default {
    props: {
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['isLargeScreen', 'cmsPage']),
        value: {
            get() {
                return getFilterValue(this.$route.query, SORT);
            },
            set(value) {
                const query = buildQuery(
                    this.$route.query,
                    { [SORT]: value },
                    { [LOCATION]: this.cmsPage?.searchSettings?.location }
                );
                this.$router.push(this.localePath({ path: this.$route.path, query }));
            },
        },
        location() {
            return getFilterValue(this.$route.query, LOCATION);
        },
        sortOptions() {
            const defaultOptions = getFilterOptions(SORT).map(option => ({
                ...option,
                label: this.$t(option.label),
            }));

            if (this.location || this.cmsPage?.searchSettings?.location) {
                defaultOptions.unshift({
                    value: sortValues.DISTANCE,
                    label: this.$t('search_form.sort_options_distance'),
                });
            }

            return defaultOptions;
        },
    },
    watch: {
        value(newValue) {
            this.resizeSelect(newValue);
        },
    },
    mounted() {
        this.$nextTick(() => {
            this.resizeSelect(this.value);
        });
    },
    methods: {
        dispatchEvent(event) {
            if (this.$gtm.enabled()) {
                window.dataLayer?.push({
                    event: 'sorting',
                    label: event.target.value,
                    locale: this.$i18n.locale,
                });
            }
        },
        resizeSelect(value) {
            const option = this.sortOptions.find(option => option.value === value);

            if (!option) {
                return;
            }

            const labelPlaceholder = option.label;
            const sortSelect = this.$el.querySelector('#sortSelect');
            const selectedText = labelPlaceholder;
            const hiddenSpan = document.createElement('span');
            hiddenSpan.style.visibility = 'hidden';
            hiddenSpan.style.whiteSpace = 'nowrap';
            hiddenSpan.style.fontWeight = 'bold';
            hiddenSpan.style.fontSize = '16px';

            if ((this.$i18n.locale === 'fi' && this.isLargeScreen) || !this.isLargeScreen) {
                hiddenSpan.style.fontSize = '14px';
            }

            if (this.$i18n.locale === 'fi' && !this.isLargeScreen) {
                hiddenSpan.style.fontSize = '13px';
            }

            hiddenSpan.textContent = selectedText;
            document.body.appendChild(hiddenSpan);
            const newWidth = hiddenSpan.getBoundingClientRect().width + 27; // Adds space on the right side (4px sapce and 11px arrow width)
            sortSelect.style.width = `${newWidth}px`;
            document.body.removeChild(hiddenSpan);
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.sort-options.input.select {
    display: inline-block;
    border: none;
    width: auto;
    background: transparent;

    select {
        padding: 0 12px;
        margin-left: -12px;
        color: $primary;
        cursor: pointer;
        line-height: 18px;
        @include font-size(14px);

        .lang-fi & {
            @include font-size(13px);
        }

        @include lg {
            @include font-size(16px);

            .lang-fi & {
                @include font-size(14px);
            }
        }

        &:hover {
            text-decoration: none;
        }

        &:disabled {
            background-color: transparent;
        }
    }

    &:after {
        right: 0;
        border-color: $primary;
        border-width: 0 2px 2px 0;
    }
}
</style>
